export const DEACTIVATED_MESSAGE = "Your account has been deactivated.";
export const TOO_MANY_ATTEMPTS_MESSAGE = "Too many attempts. Please try again later.";

interface ErrorResponse {
  status?: number;
  data?: { message?: unknown; errors?: unknown; code?: unknown };
}

function responseOf(error: unknown): ErrorResponse | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }

  return (error as { response?: ErrorResponse }).response;
}

export function isDeactivatedAdminError(error: unknown): boolean {
  const response = responseOf(error);
  const message = response?.data?.message;

  return response?.status === 403 && typeof message === "string" && message.toLowerCase().includes("deactivated");
}

export function isRateLimitedError(error: unknown): boolean {
  return responseOf(error)?.status === 429;
}

export function validationMessages(error: unknown): Record<string, string> {
  const response = responseOf(error);
  const errors = response?.data?.errors;

  if (response?.status !== 422 || typeof errors !== "object" || errors === null) {
    return {};
  }

  const messages: Record<string, string> = {};

  for (const [field, value] of Object.entries(errors)) {
    const first = Array.isArray(value) ? value.find((item): item is string => typeof item === "string") : undefined;

    if (first !== undefined) {
      messages[field] = first;
    }
  }

  return messages;
}

export function messageForCode(error: unknown, code: string): string | null {
  const data = responseOf(error)?.data;

  return data?.code === code && typeof data.message === "string" && data.message !== "" ? data.message : null;
}

export const NOT_DECIDABLE_MESSAGE = "This item cannot be approved any more.";

export function isNotDecidableError(error: unknown): boolean {
  const response = responseOf(error);

  return response?.status === 409 && response.data?.code === "moderation_item_not_decidable";
}

interface FormValidationErrors {
  fieldErrors: Record<string, string>;
  banner: string;
}

export function formValidationErrors(error: unknown, renderedFields: readonly string[]): FormValidationErrors | null {
  const fieldErrors = validationMessages(error);

  if (Object.keys(fieldErrors).length === 0) {
    return null;
  }

  const banner = Object.entries(fieldErrors)
    .filter(([field]) => !renderedFields.includes(field))
    .map(([, message]) => message)
    .join(" ");

  return { fieldErrors, banner };
}

export function authErrorMessage(error: unknown, fallback: string): string {
  if (isRateLimitedError(error)) {
    return TOO_MANY_ATTEMPTS_MESSAGE;
  }

  if (isDeactivatedAdminError(error)) {
    return DEACTIVATED_MESSAGE;
  }

  return fallback;
}
